/**
 * JsonViewer — fetch a JSON URL and render it pretty-printed in a <pre>.
 *
 * Replaces the iframe approach which silently failed on cross-origin
 * presigned URLs in some browsers (the user saw an empty viewer with
 * just Chrome's "Pretty-print" checkbox). Doing the fetch + parse
 * ourselves keeps the JSON visible in our DOM, lets us monospace-style
 * it consistently with the dark theme, and gives us a deterministic
 * loading / error state instead of a blank iframe.
 */
import React, { useEffect, useState } from 'react';

export default function JsonViewer({ url, fileName }) {
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!url) return undefined;
    let cancelled = false;
    setLoading(true); setError(''); setText('');
    (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`fetch failed: ${res.status}`);
        const raw = await res.text();
        // Try to pretty-print; if the file isn't valid JSON, show the
        // raw text rather than crashing.
        let pretty;
        try {
          pretty = JSON.stringify(JSON.parse(raw), null, 2);
        } catch {
          pretty = raw;
        }
        if (!cancelled) {
          setText(pretty);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || String(e));
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [url]);

  if (loading) {
    return <div className="json-viewer json-viewer-loading">Loading {fileName}…</div>;
  }
  if (error) {
    return (
      <div className="json-viewer json-viewer-error">
        <div>Couldn't load JSON ({error}).</div>
        <a className="modal-btn modal-btn-primary" href={url} target="_blank" rel="noopener noreferrer" download={fileName}>Download</a>
      </div>
    );
  }
  return (
    <pre className="json-viewer">
      {text}
    </pre>
  );
}
